import React, { useMemo, useState, useEffect } from "react";
import swal from "sweetalert";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  FormGroup,
  Label,
  Input,
  UncontrolledTooltip,
  Badge,
  Button,
} from "reactstrap";
import { Link } from "react-router-dom";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import TableContainer from "../../components/Common/TableContainer";
import { changeStatus, delPlace, getAllPlaces } from "helpers/requests";
import { Oval } from "react-loader-spinner";

const PlacesPage = (props) => {
  useEffect(() => {
    document.title = "Places Page";
  }, []);

  const [places, setPlaces] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(() => localStorage.getItem("pl_search") || "");
  const [availabilityFilter, setAvailabilityFilter] = useState(() => localStorage.getItem("pl_avail") || "all");

  // Fetch places using getAllPlaces function
  useEffect(() => {
    const fetchPlaces = async () => {
      try {
        const response = await getAllPlaces();
        setPlaces(response);
      } catch (error) {
        console.error("Error fetching places:", error);
        swal("Error!", "Could not fetch places data.", "error");
      } finally {
        setIsLoading(false);
      }
    };

    fetchPlaces();
  }, []);

  // Persist filters
  useEffect(() => {
    localStorage.setItem("pl_search", searchTerm);
    localStorage.setItem("pl_avail", availabilityFilter);
  }, [searchTerm, availabilityFilter]);

  const filteredPlaces = useMemo(() => {
    let filteredData = places;

    // Filter by search term
    if (searchTerm) {
      filteredData = filteredData.filter((place) =>
        place.code.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by availability
    if (availabilityFilter !== "all") {
      const isAvailable = availabilityFilter === "available";
      filteredData = filteredData.filter(
        (place) => place.available === isAvailable
      );
    }

    return filteredData;
  }, [places, searchTerm, availabilityFilter]);

  const handleSelectAll = () => {
    if (isSelectAllChecked) {
      setSelectedRows([]);
    } else {
      setSelectedRows(places.map((place) => place.id));
    }
    setIsSelectAllChecked(!isSelectAllChecked);
  };

  const handleRowSelect = (id) => {
    if (selectedRows.includes(id)) {
      setSelectedRows(selectedRows.filter((rowId) => rowId !== id));
    } else {
      setSelectedRows([...selectedRows, id]);
    }
  };

  const handleCheck = async (placeData) => {
    try {
      const confirmation = await swal({
        title: "Are you sure?",
        text: "This will toggle the availability status of the selected place.",
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });
  
      if (confirmation) {
        await changeStatus(placeData.id); // Call the changeStatus API
  
        // Toggle the `available` status locally
        setPlaces((prevPlaces) =>
          prevPlaces.map((place) =>
            place.id === placeData.id
              ? { ...place, available: !place.available }
              : place
          )
        );
  
        swal("Success!", "The status has been updated.", "success");
      } else {
        swal("Action canceled");
      }
    } catch (error) {
      console.error("Error updating place status:", error);
      swal("Error!", "Failed to update the status.", "error");
    }
  };

  const handleDelete = (reservation) => {
    swal({
      title: "Êtes-vous sûr?",
      text: "Une fois supprimé, vous ne pourrez pas récupérer cette Place",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async(willDelete) => {
      if (willDelete) {

        await delPlace(reservation.id)
      
        const response = await getAllPlaces();
        setPlaces(response);

        swal("Place supprimée!", { icon: "success" });
      } else {
        swal("Suppression annulée");
      }
    });
  };

  const columns = useMemo(
    () => [
      {
        header: () => (
          <FormGroup check className="font-size-16">
            <Label check>
              <Input
                type="checkbox"
                id="checkAll"
                checked={isSelectAllChecked}
                onChange={handleSelectAll}
              />
            </Label>
          </FormGroup>
        ),
        accessorKey: "id",
        cell: (cellProps) => (
          <FormGroup check className="font-size-16">
            <Label check>
              <Input
                type="checkbox"
                checked={selectedRows.includes(cellProps.row.original.id)}
                onChange={() => handleRowSelect(cellProps.row.original.id)}
              />
            </Label>
          </FormGroup>
        ),
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Code",
        accessorKey: "code",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Max Adults",
        accessorKey: "max_adults",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Max Children",
        accessorKey: "max_children",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Room Price",
        accessorKey: "max_additional",
        cell: (cellProps) => {
          const price = cellProps.row.original.max_additional;
          return `${price} DZD`;
        },
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Travel Price Adult",
        accessorKey: "price",
        cell: (cellProps) => {
          const price = cellProps.row.original.price;
          return `${price} DZD`;
        },
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Travel Price Child",
        accessorKey: "price_child",
        cell: (cellProps) => {
          const priceChild = cellProps.row.original.price_child;
          return `${priceChild} DZD`;
        },
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Status",
        accessorKey: "available",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cellProps) => (
          <Badge
            color={cellProps.row.original.available ? "success" : "danger"}
            className="font-size-12"
          >
            {cellProps.row.original.available ? "Available" : "Unavailable"}
          </Badge>
        ),
      },
      {
        header: "Action",
        accessorKey: "action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cellProps) => (
          <div className="d-flex gap-3">

      <Link
              to="#"
              className="text-danger"
              onClick={() => handleDelete(cellProps.row.original)}
            >
              <i className="mdi mdi-delete font-size-18" id={`deletetooltip-${cellProps.row.original.id}`} />
              <UncontrolledTooltip placement="top" target={`deletetooltip-${cellProps.row.original.id}`}>
                Delete
              </UncontrolledTooltip>
            </Link>

          </div>
        ),
      },
    ],
    [selectedRows, isSelectAllChecked, places]
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            title={props.t("Places Page")}
            breadcrumbItem={props.t("Places Page")}
          />
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <CardTitle style={{ marginBottom: "40px" }}>
                    {props.t("Places Information")}
                  </CardTitle>

                  {/* Filters */}
                  <Row className="mb-3">
                    
                    <Col md="4">
                      <FormGroup>
                        <Label for="availabilitySelect">
                          Filter by Availability
                        </Label>
                        <Input
                          type="select"
                          id="availabilitySelect"
                          value={availabilityFilter}
                          onChange={(e) =>
                            setAvailabilityFilter(e.target.value)
                          }
                        >
                          <option value="all">All</option>
                          <option value="available">Available</option>
                          <option value="unavailable">Unavailable</option>
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md="4">
                      <FormGroup>
                        <Label for="searchInput">Search</Label>
                        <Input
                          type="text"
                          id="searchInput"
                          placeholder="Search by code"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="4" className="d-flex align-items-end justify-content-center mb-2">
                    <Button
                      color="success"
                      className="btn-rounded waves-effect waves-light mb-2"
                      onClick={() => { window.location.href = 'places/create' }}
                    >
                      Ajoute une Options
                    </Button>
                  </Col>
                  </Row>

                  {isLoading ? (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "300px",
                      }}
                    >
                      <Oval
                        height={80}
                        width={80}
                        color="#556ee6"
                        visible={true}
                        ariaLabel="oval-loading"
                        secondaryColor="#4fa94d"
                        strokeWidth={2}
                        strokeWidthSecondary={2}
                      />
                    </div>
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={filteredPlaces}
                      isGlobalFilter={false}
                      isAddButton={false}
                      isPagination={true}
                      SearchPlaceholder="Cherchez un lieu"
                      buttonClass="btn btn-success btn-rounded waves-effect waves-light mb-2 me-2 addOrder-modal"
                      buttonName="Ajoute un lieu"
                      tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline"
                      theadClass="table-light"
                      pagination="pagination"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

PlacesPage.propTypes = {
  t: PropTypes.any,
};

export default withTranslation()(PlacesPage);
